import React, { useMemo, useState, useRef, useCallback, useEffect, useContext } from 'react';
import { Group, GROUP_COLORS, Pouch } from '../types';
import { getPathBounds, calculateGroupSubtotal, isGroupMergeDisabled, getContainedGroups, generateRoundedRectPath, findGroupInclusiveGtp, getGroupSerialNumber } from '../logic/GroupLogic';
import { LayerAwareCounter } from './LayerAwareCounter';
import { Sheet } from '../types';
import { appendGuideToTooltip } from '../utils/layerValueTooltip';
import { GroupDisplayTotals } from '../utils/groupDisplayTotals';
import { getModifierKeys } from '../App';
import {
  formatTooltip,
  formatGroupGuideEditPcExtrasHtml,
  formatGroupGuideDeletePcExtrasHtml,
  formatKeyDescLine,
  formatKeysLeftWithDesc,
} from '../utils/tooltipFormatter';
import { handleConfirmingDeleteButtonRelease } from '../utils/confirmingDeleteButton';
import { resolveGroupColorHotkey, GROUP_COLOR_HOTKEY_DISPLAY_ORDER } from '../utils/groupColorHotkeys';
import { isEditableElement, isTableKeyboardContextActive } from '../utils/pouchColorHotkeys';
import { GuideContext } from '../contexts/GuideContext';
import { useLocale } from '../contexts/LocaleContext';
import { changeGroupColor, enableMuteSymbol } from '../i18n';
import {
  canMergeGroupPouchesToScalar,
  getGroupScalarMergeLeaves,
} from '../logic/SplitPouchLogic';
import './styles/GroupComponent.scss';


interface GroupComponentProps {
  group: Group;
  lastGroupColor: string;
  pouches: Pouch[];
  onMouseDown: (e: React.MouseEvent, targetId?: string, targetType?: 'pouch' | 'group' | 'resize-handle') => void;
  onMouseUp: (e: MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent, targetId?: string, targetType?: 'pouch' | 'group' | 'resize-handle') => void;
  isInitialLoading?: boolean;
  isSheetSwitching?: boolean;
  onMergeGroup?: (groupId: string, isShiftPressed?: boolean, isAltControlPressed?: boolean) => void;
  /** 2階: 同一 value の葉を 1 パウチ（×合計マルチ）に統合（グループ維持） */
  onMergeGroupScalar?: (groupId: string) => void;
  /** 内包GTPを値/マルチにレンダリング（Merge Group Total Pouch） */
  onMergeGroupTotalPouch?: (groupId: string, mode: 'value' | 'multi') => void;
  onMergeGroupTotalPouchPreview?: (groupId: string, mode: 'value' | 'multi' | null) => void;
  onExportGroup?: (groupId: string) => void;
  onResetInteraction?: () => void;
  onToggleGroupSign?: (groupId: string) => void;
  onChangeGroupColor?: (groupId: string, isShiftPressed?: boolean, isCtrlOptionPressed?: boolean) => void;
  /** キーボード: GROUP_COLORS の id を直接指定 */
  onSetGroupColorById?: (groupId: string, colorId: string) => void;
  isTouchDevice?: boolean;
  onDeleteGroup?: (groupId: string, isCtrlOptionPressed?: boolean, isShiftPressed?: boolean, isAltControlPressed?: boolean) => void;
  onUpdateGroupTitle?: (groupId: string, title: string) => void;
  onEditConfirm?: () => void;
  onCreateGroupTotalPouch?: (groupId: string, clientX: number, clientY: number) => void; // グループトータルパウチ作成用
  onCheckGroupDragging?: (groupId: string) => boolean; // グループのドラッグ状態をチェック
  allGroups?: Group[]; // マージボタンの条件判定に使用
  isTableSelected?: boolean; // テーブルから選択されているかのフラグ
  sheetTitle?: string; // シート名（TOTAL表示のグループのプレースホルダー用）
  onGroupHover?: (groupId: string | null) => void; // グループホバー時のコールバック
  hoveredGroupId?: string | null; // 現在ホバーされているグループID
  /** ホバー対象の同期判定用（キーホットキーで state の遅延を避ける） */
  hoveredGroupIdRef?: React.MutableRefObject<string | null>;
  gtpHoveredGroupId?: string | null; // GTPホバー時のグループID
  allPouches?: Pouch[]; // 全パウチリスト（グループ外のGTPも含む）
  currentSheet?: Sheet;
  /** ActiveSheet で一括計算した小計・TOTAL 表示（B） */
  displayTotals: GroupDisplayTotals;
}

interface PendingGroupProps {
  pendingPath: string;
  lastGroupColor: string;
  isTooSmall?: boolean;
}

// グループの主要コンポーネント
export const GroupComponent: React.FC<GroupComponentProps> = ({ 
  group, 
  pouches, 
  onMouseDown,
  onMouseUp,
  onTouchStart,
  isInitialLoading = false,
  isSheetSwitching,
  onMergeGroup = () => {},
  onMergeGroupScalar = () => {},
  onMergeGroupTotalPouch = () => {},
  onMergeGroupTotalPouchPreview = () => {},
  onExportGroup = () => {},
  onResetInteraction = () => {},
  onToggleGroupSign = () => {},
  onChangeGroupColor = () => {},
  onSetGroupColorById = () => {},
  isTouchDevice = false,
  onDeleteGroup = () => {},
  onUpdateGroupTitle = () => {},
  onEditConfirm = () => {},
  onCreateGroupTotalPouch = () => {},
  onCheckGroupDragging = () => false,
  allGroups = [], // デフォルト値として空配列を設定
  isTableSelected = false, // デフォルト値はfalse
  sheetTitle,
  onGroupHover = () => {},
  hoveredGroupId,
  hoveredGroupIdRef,
  gtpHoveredGroupId,
  allPouches = [],
  currentSheet,
  displayTotals
}) => {
  const _isGuideEnabled = useContext(GuideContext);
  const { appCopy: t, tooltipCopy: tt, locale } = useLocale();
  // パス境界を取得（エラーハンドリング付き）
  const pathBounds = getPathBounds(group.path);
  
  // パス境界の取得に失敗した場合の処理
  if (!pathBounds) {
    console.error('GroupComponent: Failed to get path bounds for group:', group.id, 'Path:', group.path);
    
    // フォールバック: デフォルトの境界を使用
    const fallbackBounds = {
      left: 0,
      top: 0,
      right: 200,
      bottom: 100
    };
    
    
    // 壊れたパスを修復を試行
    try {
      const repairedPath = generateRoundedRectPath(0, 0, 200, 100);
      
      // 修復されたパスでグループを更新（非同期で実行）
      // TODO: onUpdateGroupPath関数を実装する必要がある
      // setTimeout(() => {
      //   if (onUpdateGroupPath) {
      //     onUpdateGroupPath(group.id, repairedPath);
      //   }
      // }, 0);
      
    } catch (repairError) {
      console.error('GroupComponent: Failed to repair path:', repairError);
    }
  }
  
  // タイトル編集用の状態
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(group.title || '');
  const editGuideTimerRef = useRef<number | null>(null);
  const [editGuideTier, setEditGuideTier] = useState<1 | 2>(1);

  const GUIDE_HOVER_DELAY_MS = 2000;
  const groupGuideHoverTimerRef = useRef<number | null>(null);
  const [groupGuideHoverTier, setGroupGuideHoverTier] = useState<0 | 1 | 2>(0);
  const resizeGuideHoverTimerRef = useRef<number | null>(null);
  const resizeGuideHoverDepthRef = useRef(0);
  const [resizeGuideHoverTier, setResizeGuideHoverTier] = useState<0 | 1 | 2>(0);
  
  // IME入力中かどうかの状態
  const [isComposing, setIsComposing] = useState(false);
  
  // タイトル入力欄のref
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  /** Esc で blur したとき履歴を積まない */
  const titleEscapeCancelRef = useRef(false);
  const wasTitleFocusedOnMouseDown = useRef(false);
  
  // 編集確定フラグ用のref
  const justConfirmedRef = useRef(false);
  const confirmTimeoutRef = useRef<number | null>(null);
  
  // 削除ボタンの長押し検出用のref
  const deleteLongPressTimerRef = useRef<number | null>(null);
  const deletePhaseTimersRef = useRef<number[]>([]);
  const deleteStartTimeRef = useRef<number>(0);
  const deleteCurrentPhaseRef = useRef<number>(0);
  const deleteModifierKeysRef = useRef<{ctrlOption: boolean; shift: boolean; altControl: boolean} | null>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  /** タッチ直後に送られる合成 mousedown/mouseup を無視する（この時刻まで mouse を無視） */
  const deleteSuppressMouseAfterTouchUntilRef = useRef<number>(0);

  // マージボタンの長押し検出用のref
  const mergeLongPressTimerRef = useRef<number | null>(null);
  const mergeStartTimeRef = useRef<number>(0);
  const mergeModifierKeysRef = useRef<{
    shift: boolean;
    altControl: boolean;
  } | null>(null);
  /** タッチ直後に送られる合成 mousedown/mouseup を無視する（この時刻まで mouse を無視） */
  const mergeSuppressMouseAfterTouchUntilRef = useRef<number>(0);

  // マージフェーズの定義
  const MERGE_PHASES = {
    NORMAL_MERGE: 0,        // 0秒: 通常のマージ（本・内・パが削除プレビュー）
    SHIFT_MERGE: 1          // 1秒: Shift+マージ（内・パが削除プレビュー）
  };

  // 内包GTP（自グループを参照するGTPがグループ内に居る状態）を検出
  // 存在するときはマージボタンを "Merge Group Total Pouch" モードに切替
  const inclusiveGtp = useMemo(() => {
    return findGroupInclusiveGtp(group, pouches);
  }, [group, pouches]);
  const hasInclusiveGtp = inclusiveGtp !== null;

  const scalarMergeLeaves = useMemo(
    () => getGroupScalarMergeLeaves(group, pouches, allGroups),
    [group, pouches, allGroups],
  );
  const canScalarMerge = useMemo(
    () => !hasInclusiveGtp && canMergeGroupPouchesToScalar(scalarMergeLeaves, currentSheet),
    [hasInclusiveGtp, scalarMergeLeaves, currentSheet],
  );

  const {
    contribution: groupSubtotalContribution,
    valueTooltipHtml: groupSubtotalValueTooltipHtml,
    isTotalGroup,
    showTimeConversion,
  } = displayTotals;

  // グローバルな修飾キー状態を追跡するためのuseEffect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        (window as any).__ctrlKeyPressed = true;
      } else if (e.key === 'Alt') {
        (window as any).__altKeyPressed = true;
      } else if (e.key === 'Shift') {
        (window as any).__shiftKeyPressed = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        (window as any).__ctrlKeyPressed = false;
      } else if (e.key === 'Alt') {
        (window as any).__altKeyPressed = false;
      } else if (e.key === 'Shift') {
        (window as any).__shiftKeyPressed = false;
      }
    };

    // 初期化
    (window as any).__ctrlKeyPressed = false;
    (window as any).__altKeyPressed = false;
    (window as any).__shiftKeyPressed = false;

    // イベントリスナーを追加
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // クリーンアップ
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  
  // 削除フェーズの定義
  const DELETE_PHASES = {
    MAIN_ONLY: 0,        // 0秒: 本削除
    CONTAINED_ONLY: 1,   // 1秒: 内削除
    CONTAINED_WITH_POUCHES: 2, // 2秒: 内・パ削除
    MAIN_WITH_CONTAINED: 3,    // 3秒: 本・内削除（パ復活）
    ALL_DELETE: 4        // 4秒: 本・内・パ削除
  };
  
  
  // 削除ボタンのtitle生成関数
  const getDeleteButtonTooltipHtml = () => {
    if (!_isGuideEnabled) return '';
    const keys = getModifierKeys();
    const s = (ch: string) => `<span style="color:#808080;font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const line = (key: string, desc: string) => formatKeyDescLine(key, desc);
    return [
      line('Double Click', `${tt.deleteGroupThisFrame} ${s('■')}`),
      line('Long Press', tt.deleteGroupProgressive),
      line(`${keys.ctrlOption}+Double Click`, `${tt.deleteGroupAll} ${s('■▣⛬')}`),
      line(`${keys.shift}+${keys.ctrlOption}+Double Click`, `${tt.deleteGroupGroups} ${s('■▣')}`),
      line(`${keys.altControl}+Double Click`, `${tt.deleteGroupInnerGroupsAndPouches} ${s('▣⛬')}`),
      line(`${keys.shift}+${keys.altControl}+Double Click`, `${tt.deleteGroupInnerGroups} ${s('▣')}`),
    ]
      .filter(Boolean)
      .join('<br/>');
  };
  
  
  // マージ用のプレビュー表示関数
  const applyMergePhase = useCallback((phase: number, save: boolean = false) => {
    if (!save) {
      
      // まず、すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
        element.classList.remove('gtp-render-fading');
      });
    }

    // 内包GTPがあるグループは、マージボタンを "GTPレンダリング" モードで動かす
    if (hasInclusiveGtp && inclusiveGtp) {
      const mode: 'value' | 'multi' = phase === MERGE_PHASES.NORMAL_MERGE ? 'value' : 'multi';
      if (!save) {
        // state 側で葉パウチを "レンダー後の値/マルチ" に書き換える（履歴には積まない）
        onMergeGroupTotalPouchPreview(group.id, mode);
        // GTP は視覚的に半透明に（state に残したまま DOM クラスで見た目のみ調整）
        const gtpEl = document.querySelector(`[data-pouch-id="${inclusiveGtp.id}"]`);
        if (gtpEl) gtpEl.classList.add('gtp-render-fading');
      } else {
        onMergeGroupTotalPouch(group.id, mode);
      }
      return;
    }

    const targets = checkDeleteTargets();
    
    switch (phase) {
      case MERGE_PHASES.NORMAL_MERGE:
        if (!save) {
          if (canScalarMerge) {
            scalarMergeLeaves.forEach(pouch => {
              const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
              if (pouchElement) {
                pouchElement.classList.add('delete-preview');
              }
            });
          } else {
            // 本・内・パが削除プレビュー
            const groupElement = document.querySelector(`[data-group-id="${group.id}"]`);
            if (groupElement) {
              groupElement.classList.add('delete-preview');
            }

            // 内包グループのプレビュー
            const containedGroups = getContainedGroups(group, allGroups);
            containedGroups.forEach(containedGroup => {
              const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
              if (containedElement) {
                containedElement.classList.add('delete-preview');
              }
            });

            // 所属パウチのプレビュー
            const pouchesInGroup = pouches.filter(p => {
              return group.pouches && group.pouches.includes(p.id);
            });
            pouchesInGroup.forEach(pouch => {
              const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
              if (pouchElement) {
                pouchElement.classList.add('delete-preview');
              }
            });
          }
        } else if (canScalarMerge) {
          onMergeGroupScalar(group.id);
        } else {
          // 3階: 合計1パウチ化（グループ削除）
          onMergeGroup(group.id, false, false);
        }
        break;
        
      case MERGE_PHASES.SHIFT_MERGE:
        if (!save) {
          // 内・パが削除プレビュー（本は除外）
          const containedGroups = getContainedGroups(group, allGroups);
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          
          // 所属パウチのプレビュー
          const pouchesInGroup = pouches.filter(p => {
            return group.pouches && group.pouches.includes(p.id);
          });
          pouchesInGroup.forEach(pouch => {
            const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
        } else {
          // 実際のマージ処理（Shift+マージ）- 本体グループは削除しない
          onMergeGroup(group.id, true, false);
        }
        break;
    }
  }, [group, pouches, allGroups, onMergeGroup, onMergeGroupScalar, canScalarMerge, scalarMergeLeaves, hasInclusiveGtp, inclusiveGtp, onMergeGroupTotalPouch, onMergeGroupTotalPouchPreview]);
  
  // マージボタンが無効化されるべきかを判断
  const isMergeDisabled = useMemo(() => {
    return isGroupMergeDisabled(group, pathBounds, allGroups, pouches);
  }, [group, pathBounds, allGroups, pouches, group._isDragging]);

  // マージ用のタイマークリア関数
  const clearMergeTimers = useCallback(() => {
    if (mergeLongPressTimerRef.current) {
      window.clearTimeout(mergeLongPressTimerRef.current);
      mergeLongPressTimerRef.current = null;
    }
    mergeStartTimeRef.current = 0;
    mergeModifierKeysRef.current = null;
  }, []);

  // マージ用の共通開始ハンドラー
  const handleMergeStart = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    e.preventDefault();

    // タッチのあとに送られる合成 mousedown が mergeStartTimeRef を立て直し、
    // 続く mouseup で通常グループマージが走る（内包GTP は既に消えている）ため無視する
    if (eventType === 'mouse' && Date.now() < mergeSuppressMouseAfterTouchUntilRef.current) {
      return;
    }

    if (isMergeDisabled) {
      return;
    }
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isShiftPressed = false;
    let isAltControlPressed = false;
    
    if (eventType === 'mouse') {
      const mouseEvent = e as React.MouseEvent;
      isShiftPressed = mouseEvent.shiftKey || false;
      isAltControlPressed = isMac ? mouseEvent.ctrlKey : mouseEvent.altKey;
    } else {
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isAltControlPressed = isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed;
    }
    
    // 修飾キー状態を保存
    mergeModifierKeysRef.current = {
      shift: isShiftPressed,
      altControl: isAltControlPressed
    };
    mergeStartTimeRef.current = Date.now();
    
    // 修飾キーが押されている場合は、開始時に修飾キー相当のプレビューを表示
    if (mergeModifierKeysRef.current) {
      const { shift, altControl } = mergeModifierKeysRef.current;
      if (shift || altControl) {
        applyMergePhase(MERGE_PHASES.SHIFT_MERGE, false);
        // 内包GTPがあるときは自動 commit させない（プレゼン/観察のため押したままにできる）
        if (!hasInclusiveGtp) {
          mergeLongPressTimerRef.current = window.setTimeout(() => {
            applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
          }, 1000);
        }
        return;
      }
    }
    
    // 修飾キーが押されていない場合は、通常の処理
    applyMergePhase(MERGE_PHASES.NORMAL_MERGE, false);
    
    // 長押し検出タイマーを開始
    mergeLongPressTimerRef.current = window.setTimeout(() => {
      // すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      
      // フェーズ1（Shift+マージ）のプレビューを表示
      applyMergePhase(MERGE_PHASES.SHIFT_MERGE, false);
    }, 1000);
  }, [isMergeDisabled, applyMergePhase, hasInclusiveGtp]);

  // マージ用の共通終了ハンドラー
  const handleMergeEnd = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    if (eventType === 'mouse' && Date.now() < mergeSuppressMouseAfterTouchUntilRef.current) {
      if (mergeLongPressTimerRef.current) {
        window.clearTimeout(mergeLongPressTimerRef.current);
        mergeLongPressTimerRef.current = null;
      }
      clearMergeTimers();
      return;
    }
    // タイマーをクリア
    if (mergeLongPressTimerRef.current) {
      window.clearTimeout(mergeLongPressTimerRef.current);
      mergeLongPressTimerRef.current = null;
    }

    // touchstart なしの終了、またはタッチ完了後に送られる合成 mouseup では
    // mergeStartTimeRef が既に 0 になっている。ここで applyMergePhase(true) すると
    // （内包GTP が初回 touchend で消えた直後）通常グループマージが誤発火する。
    if (mergeStartTimeRef.current <= 0) {
      clearMergeTimers();
      return;
    }

    const holdDuration = Date.now() - mergeStartTimeRef.current;

    if (holdDuration >= 1000) {
      // 長押し検出済み

      // 修飾キーが押されている場合は、修飾キー相当のフェーズで保存
      if (mergeModifierKeysRef.current) {
        const { shift, altControl } = mergeModifierKeysRef.current;
        if (shift || altControl) {
          applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
          clearMergeTimers();
          return;
        }
      }

      // 修飾キーが押されていない場合は、フェーズ1で保存
      applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
    } else {
      // 短いクリックの場合、修飾キーの状態に応じて処理
      if (mergeModifierKeysRef.current) {
        const { shift, altControl } = mergeModifierKeysRef.current;
        if (shift || altControl) {
          // 修飾キーが押されている場合は、Shift+マージ
          applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
        } else {
          // 修飾キーが押されていない場合は、通常のマージ
          applyMergePhase(MERGE_PHASES.NORMAL_MERGE, true);
        }
      } else {
        // 通常のマージ処理
        applyMergePhase(MERGE_PHASES.NORMAL_MERGE, true);
      }
    }

    clearMergeTimers();

    // タッチ完了後の合成マウス列を無視（mousedown が来ると ref が再設定される）
    if (eventType === 'touch') {
      mergeSuppressMouseAfterTouchUntilRef.current = Date.now() + 550;
    }
  }, [applyMergePhase, clearMergeTimers]);

  // マージ用のキャンセルハンドラー
  const handleMergeCancel = useCallback(() => {
    clearMergeTimers();
    
    // プレビュークラスをクリア
    const allGroupElements = document.querySelectorAll('[data-group-id]');
    allGroupElements.forEach(element => {
      element.classList.remove('delete-preview');
    });
    const allPouchElements = document.querySelectorAll('[data-pouch-id]');
    allPouchElements.forEach(element => {
      element.classList.remove('delete-preview');
      element.classList.remove('gtp-render-fading');
    });

    // GTPレンダリングのプレビュー state を元に戻す
    if (hasInclusiveGtp) {
      onMergeGroupTotalPouchPreview(group.id, null);
    }
  }, [clearMergeTimers, hasInclusiveGtp, group.id, onMergeGroupTotalPouchPreview]);

  // カラー変更ボタンの長押し検出用のref
  const colorLongPressTimerRef = useRef<number | null>(null);
  const colorStartTimeRef = useRef<number>(0);
  const colorModifierKeysRef = useRef<{
    shift: boolean;
    ctrlOption: boolean;
  } | null>(null);

  // カラー変更用のタイマークリア関数
  const clearColorTimers = useCallback(() => {
    if (colorLongPressTimerRef.current) {
      window.clearTimeout(colorLongPressTimerRef.current);
      colorLongPressTimerRef.current = null;
    }
    colorStartTimeRef.current = 0;
    colorModifierKeysRef.current = null;
  }, []);

  // カラー変更用の共通開始ハンドラー
  const handleColorStart = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    e.preventDefault();
    
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isShiftPressed = false;
    let isCtrlOptionPressed = false;
    
    if (eventType === 'mouse') {
      const mouseEvent = e as React.MouseEvent;
      isShiftPressed = mouseEvent.shiftKey || false;
      isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
    } else {
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
    }
    
    
    // 修飾キー状態を保存
    colorModifierKeysRef.current = {
      shift: isShiftPressed,
      ctrlOption: isCtrlOptionPressed
    };
    colorStartTimeRef.current = Date.now();
    
    // 修飾キーが押されている場合は、開始時に修飾キー相当の動作を実行
    if (colorModifierKeysRef.current) {
      const { shift, ctrlOption } = colorModifierKeysRef.current;
      if (shift || ctrlOption) {
        if (shift) {
          // Shift+クリック: 逆ローテーション
          onChangeGroupColor(group.id, true, false);
        } else if (ctrlOption) {
          // Ctrl/Option+クリック: ミュート/元に戻す
          onChangeGroupColor(group.id, false, true);
        }
        return; // 修飾キーが押されている場合は長押しタイマーを開始しない
      }
    }
    
    // 修飾キーが押されていない場合は、長押し検出タイマーを開始
    colorLongPressTimerRef.current = window.setTimeout(() => {
      // 修飾キーなし+長押し: ミュート/元に戻す
      onChangeGroupColor(group.id, false, true);
    }, 1000);
  }, [group.id, onChangeGroupColor]);

  // カラー変更用の共通終了ハンドラー
  const handleColorEnd = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    
    
    // タイマーをクリア
    if (colorLongPressTimerRef.current) {
      window.clearTimeout(colorLongPressTimerRef.current);
      colorLongPressTimerRef.current = null;
    }
    
    // 修飾キーが押されていた場合は、既に開始時に処理済みなので何もしない
    if (colorModifierKeysRef.current) {
      const { shift, ctrlOption } = colorModifierKeysRef.current;
      if (shift || ctrlOption) {
        clearColorTimers();
        return;
      }
    }
    
    if (colorStartTimeRef.current > 0) {
      const holdDuration = Date.now() - colorStartTimeRef.current;
      
      if (holdDuration >= 1000) {
        // 長押し検出済み - 既に長押しタイマーで処理済み
      } else {
        // 短いクリックの場合、通常のカラー変更処理
        onChangeGroupColor(group.id, false, false);
      }
    } else {
      // 通常のクリック処理（短いクリック）
      onChangeGroupColor(group.id, false, false);
    }
    
    clearColorTimers();
  }, [group.id, onChangeGroupColor, clearColorTimers]);

  // カラー変更用のキャンセルハンドラー
  const handleColorCancel = useCallback(() => {
    clearColorTimers();
  }, [clearColorTimers]);

  const getGroupColorTooltipHtml = () => {
    if (!_isGuideEnabled) return '';
    const MUTE_COLOR = '#666666';
    const allColors = [...GROUP_COLORS.map(c => ({ id: c.id, value: c.value })), { id: 'mute', value: MUTE_COLOR }];
    const currentColor = group.color || GROUP_COLORS[0].id;
    const currentIndex = allColors.findIndex(c => c.id === currentColor);
    const nextIndex = (currentIndex + 1) % allColors.length;
    const prevIndex = (currentIndex - 1 + allColors.length) % allColors.length;
    const sym = (color: string, ch: string) => `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const colorLabel = (idx: number) => allColors[idx].id === 'mute'
      ? { isMuteTarget: true, html: enableMuteSymbol(sym(MUTE_COLOR, '▩'), locale) }
      : { isMuteTarget: false, html: changeGroupColor(sym(allColors[idx].value, '■'), locale) };
    const keys = getModifierKeys();
    const isMute = currentColor === 'mute';
    const muteLine = isMute
      ? `Disable Mute ${sym(GROUP_COLORS.find(c => c.id === (group.originalColor || 'yellow'))?.value || GROUP_COLORS[0].value, '■')}`
      : enableMuteSymbol(sym(MUTE_COLOR, '▩'), locale);
    return [
      formatKeyDescLine('Click', colorLabel(nextIndex).html),
      formatKeysLeftWithDesc(`${formatTooltip(keys.shift)}${formatTooltip('Click')}`, colorLabel(prevIndex).html),
      formatKeysLeftWithDesc(
        `${formatTooltip(keys.ctrlOption)}${formatTooltip('Click / Long Press')}`,
        muteLine,
      ),
    ]
      .filter(Boolean)
      .join('<br/>');
  };

  const groupGuideMovementFullHtml = useMemo(() => {
    const keys = getModifierKeys();
    return formatTooltip(
      [
        `Drag: ${tt.dragMoveGroup}`,
        `${keys.shift}+Drag: ${tt.dragMoveGroupFrameOnly}`,
        `Long Press Drag: ${tt.longPressDragCloneGroup}`,
        `${keys.shift}+Long Press Drag: ${tt.longPressDragCloneGroupFrameOnly}`,
        `${keys.ctrlOption}+Drag: ${tt.cloneGroupDrag}`,
        `${keys.shift}+${keys.ctrlOption}+Drag: ${tt.cloneGroupFrameOnlyDrag}`,
      ].join('\n'),
    );
  }, [tt]);

  const groupGuideColorExtrasHtml = useMemo(() => {
    if (!_isGuideEnabled) return '';
    const MUTE_COLOR = '#666666';
    const allColors = [...GROUP_COLORS.map(c => ({ id: c.id, value: c.value })), { id: 'mute', value: MUTE_COLOR }];
    const currentColor = group.color || GROUP_COLORS[0].id;
    const currentIndex = allColors.findIndex(c => c.id === currentColor);
    const nextIndex = (currentIndex + 1) % allColors.length;
    const prevIndex = (currentIndex - 1 + allColors.length) % allColors.length;
    const sym = (color: string, ch: string) => `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const colorLabel = (idx: number) =>
      allColors[idx].id === 'mute'
        ? enableMuteSymbol(sym(MUTE_COLOR, '▩'), locale)
        : changeGroupColor(sym(allColors[idx].value, '■'), locale);
    const keys2 = getModifierKeys();
    const rotateNext = formatKeyDescLine('C', colorLabel(nextIndex));
    const rotatePrev = formatKeysLeftWithDesc(
      `${formatTooltip(keys2.shift)}${formatTooltip('C')}`,
      colorLabel(prevIndex),
    );
    const colorCircle = (idx: number) => {
      const c = GROUP_COLORS[idx];
      return `<span style="color:${c.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">■</span>`;
    };
    const directRow = `<span class="tt-desc">${GROUP_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
      if (colorId === 'mute') {
        return `<kbd>${letter}</kbd><span style="color:${MUTE_COLOR};font-size:1.3em;line-height:1;vertical-align:-0.05em">▩</span>`;
      }
      const idx = GROUP_COLORS.findIndex(c => c.id === colorId);
      const circ = idx >= 0 ? colorCircle(idx) : '';
      return `<kbd>${letter}</kbd>${circ}`;
    }).join(' ')}</span>`;
    return `${rotateNext}<br/>${rotatePrev}<br/>${directRow}`;
  }, [group.color, group.originalColor, _isGuideEnabled, locale]);

  /** リサイズハンドル tier2 と同じキーボード追記（各ボタン・サブトータルにも使用） */
  const groupHoverTier2KeyboardExtrasHtml = useMemo(() => {
    if (isTouchDevice || !_isGuideEnabled) return '';
    return `${formatGroupGuideEditPcExtrasHtml()}<br/>${groupGuideColorExtrasHtml}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
  }, [isTouchDevice, _isGuideEnabled, groupGuideColorExtrasHtml]);

  const groupExportTooltipHtml = useMemo(() => {
    const p1 = formatTooltip(`Click: ${tt.exportSummaryText}`);
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupToggleSignTooltipHtml = useMemo(() => {
    const p1 = formatTooltip(`Click: ${tt.toggleSignAllPouchesInGroup}`);
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    const escLine = formatKeyDescLine('Esc', tt.toggleSignAllPouchesInGroup);
    return `${p1}<br/>${escLine}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupColorButtonTooltipHtml = useMemo(() => {
    const p1 = getGroupColorTooltipHtml();
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    if (!p1) return groupHoverTier2KeyboardExtrasHtml;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml, group.color, group.originalColor]);

  const groupMergeTooltipHtml = useMemo(() => {
    const s = (ch: string) => `<span style="color:#808080;font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const line = (key: string, desc: string) => formatKeyDescLine(key, desc);
    // 内包GTPがあるときは "Merge Group Total Pouch" モードのガイドに切替
    const p1 = hasInclusiveGtp
      ? [
          line('Click', tt.mergeGtpToValues),
          line(`${getModifierKeys().shift}+Click`, tt.mergeGtpToMultipliers),
          line('Long Press', tt.mergeGtpToMultipliersLongPress),
        ]
          .filter(Boolean)
          .join('<br/>')
      : [
          line('Click', `Merge ${s('■▣⛬')} All`),
          line('Long Press', 'Progressive Merge'),
          line(`${getModifierKeys().shift}+Click`, `Merge ${s('▣⛬')} Inner Groups &amp; Pouches`),
        ]
          .filter(Boolean)
          .join('<br/>');
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml, hasInclusiveGtp]);

  const groupDeleteButtonTooltipHtml = useMemo(() => {
    const p1 = getDeleteButtonTooltipHtml();
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    if (!p1) return groupHoverTier2KeyboardExtrasHtml;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupAddGtpTooltipHtml = useMemo(() => {
    const p1 = formatTooltip(`Click: ${tt.addGroupTotalPouch}`);
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  // パウチ同様: 値詳細を先頭にし、その下に操作ガイドを連結（大/小で共通）
  const groupSubtotalCompositeTooltipHtml = useMemo(() => {
    return appendGuideToTooltip(groupSubtotalValueTooltipHtml, groupAddGtpTooltipHtml);
  }, [groupSubtotalValueTooltipHtml, groupAddGtpTooltipHtml]);

  const groupContainerTooltipHtml = useMemo(() => {
    if (isTitleEditing) return '';
    const phase1 = formatTooltip(`Double Click: ${tt.doubleClickCreatePouch}\nDrag: ${tt.dragMoveGroup}`);
    if (groupGuideHoverTier < 2) {
      return phase1;
    }
    if (isTouchDevice) {
      if (!_isGuideEnabled) return phase1;
      return groupGuideMovementFullHtml;
    }
    if (!_isGuideEnabled) {
      return phase1;
    }
    const escGroupToggle =
      group.pouches && group.pouches.length > 0
        ? `<br/>${formatKeyDescLine('Esc', tt.toggleSignAllPouchesInGroup)}`
        : '';
    return `${groupGuideMovementFullHtml}<br/>${formatGroupGuideEditPcExtrasHtml()}${escGroupToggle}<br/>${groupGuideColorExtrasHtml}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
  }, [
    isTitleEditing,
    groupGuideHoverTier,
    isTouchDevice,
    groupGuideMovementFullHtml,
    groupGuideColorExtrasHtml,
    _isGuideEnabled,
    group.pouches,
  ]);

  const groupResizeTooltipHtml = useMemo(() => {
    if (isTitleEditing) return '';
    const r1 = formatTooltip(`Drag: ${tt.dragResizeGroup}`);
    if (resizeGuideHoverTier < 2) {
      return r1;
    }
    if (isTouchDevice) {
      return r1;
    }
    if (!_isGuideEnabled) {
      return r1;
    }
    return `${r1}<br/>${formatGroupGuideEditPcExtrasHtml()}<br/>${groupGuideColorExtrasHtml}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
  }, [isTitleEditing, resizeGuideHoverTier, isTouchDevice, groupGuideColorExtrasHtml, _isGuideEnabled]);

  /** グループタイトル欄ホバー: 初期は Click のみ、2 秒後はドラッグ＋Edit→色→Delete */
  const groupTitleLongHoverExtrasHtml = useMemo(() => {
    if (!_isGuideEnabled || isTouchDevice) return '';
    return [
      formatTooltip(`Drag: ${tt.dragMoveGroup}`),
      formatGroupGuideEditPcExtrasHtml(),
      groupGuideColorExtrasHtml,
      formatGroupGuideDeletePcExtrasHtml(),
    ].join('<br/>');
  }, [_isGuideEnabled, isTouchDevice, groupGuideColorExtrasHtml]);

  const groupTitleTooltipHtml = useMemo(() => {
    if (isTitleEditing) return '';
    const phase1 = formatTooltip(`Click: ${tt.editGroupTitle}`);
    if (groupGuideHoverTier < 2) {
      return phase1;
    }
    if (isTouchDevice) {
      return phase1;
    }
    if (!_isGuideEnabled) {
      return phase1;
    }
    if (groupTitleLongHoverExtrasHtml) {
      return `${phase1}<br/>${groupTitleLongHoverExtrasHtml}`;
    }
    return phase1;
  }, [isTitleEditing, groupGuideHoverTier, isTouchDevice, groupTitleLongHoverExtrasHtml, _isGuideEnabled]);

  /** グループタイトル編集中（パウチと同様: Enter/Esc → 2 秒後に色） */
  const groupEditModeTooltipHtml = useMemo(() => {
    if (!isTitleEditing) return '';
    if (!_isGuideEnabled) return '';
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const enterHint = formatKeyDescLine('Enter', esc(tt.commitTitle));
    const escHint = formatKeyDescLine('Esc', esc(tt.discard));
    const tier1 = `${enterHint}<br/>${escHint}`;
    if (editGuideTier < 2) {
      return tier1;
    }
    if (isTouchDevice) {
      return tier1;
    }
    if (groupGuideColorExtrasHtml) {
      return `${tier1}<br/>${groupGuideColorExtrasHtml}`;
    }
    return tier1;
  }, [isTitleEditing, _isGuideEnabled, editGuideTier, isTouchDevice, groupGuideColorExtrasHtml]);

  useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    if (isTitleEditing) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const activeId = hoveredGroupIdRef?.current ?? hoveredGroupId;
      if (activeId !== group.id) {
        return;
      }
      if (group._isDragging) {
        return;
      }
      if (document.activeElement === titleInputRef.current) {
        return;
      }
      if (isComposing) {
        return;
      }
      if (isEditableElement(e.target as Element)) {
        return;
      }
      if (e.key === 'F2' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        setIsTitleEditing(true);
        requestAnimationFrame(() => {
          titleInputRef.current?.focus();
          titleInputRef.current?.select();
        });
        return;
      }
      const action = resolveGroupColorHotkey(e);
      if (!action) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (action.type === 'rotate') {
        onChangeGroupColor(group.id, action.reverse, false);
        return;
      }
      if (action.type === 'muteToggle') {
        onChangeGroupColor(group.id, false, true);
        return;
      }
      if (action.type === 'direct') {
        onSetGroupColorById(group.id, action.colorId);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredGroupId,
    hoveredGroupIdRef,
    group.id,
    group._isDragging,
    isTitleEditing,
    isComposing,
    onChangeGroupColor,
    onSetGroupColorById,
  ]);

  // PC・グループホバー時: Delete / Backspace = 削除ボタンの短クリックと同じ修飾キー（編集中・編集系フォーカス時は無効）
  useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.repeat || (e.key !== 'Delete' && e.key !== 'Backspace')) {
        return;
      }
      if (isComposing) {
        return;
      }
      if (isTableKeyboardContextActive()) {
        return;
      }
      const activeId = hoveredGroupIdRef?.current ?? hoveredGroupId;
      if (activeId !== group.id) {
        return;
      }
      if (group._isDragging) {
        return;
      }
      if (isTitleEditing) {
        return;
      }
      if (document.activeElement === titleInputRef.current) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCtrlOptionPressed = isMac ? e.altKey : e.ctrlKey;
      const isShiftPressed = e.shiftKey || false;
      const isAltControlPressed = isMac ? e.ctrlKey : e.altKey;
      onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredGroupId,
    hoveredGroupIdRef,
    group.id,
    group._isDragging,
    isTitleEditing,
    isComposing,
    onDeleteGroup,
  ]);

  // 削除対象の存在チェック
  const checkDeleteTargets = useCallback(() => {
    // deleteGroupWithContentsと同じ方法でパウチの所属をチェック
    const pouchesInGroup = pouches.filter(p => {
      // 方法1: グループのpouchesプロパティをチェック
      const isInGroupPouches = group.pouches && group.pouches.includes(p.id);
      // groupIdsは廃止済み
      return isInGroupPouches;
    });
    
    // 正しい内包グループのチェック
    const containedGroups = getContainedGroups(group, allGroups);
    
    
    return {
      hasContainedGroups: containedGroups.length > 0,
      hasPouches: pouchesInGroup.length > 0,
      canDeleteMain: true,
      canDeleteContained: containedGroups.length > 0,
      canDeletePouches: pouchesInGroup.length > 0
    };
  }, [group, pouches, allGroups]);
  
  // 削除フェーズの適用（可視化のみ）
  const applyDeletePhase = useCallback((phase: number, save: boolean = false) => {
    deleteCurrentPhaseRef.current = phase;
    
    // 削除状態の可視化（animationflags処理）
    // 実際の削除は行わず、画面反映のみ
    // プレビューフェーズのみログ
    if (!save) {
      const phaseNames = {
        [DELETE_PHASES.MAIN_ONLY]: 'MAIN_ONLY (本削除)',
        [DELETE_PHASES.CONTAINED_ONLY]: 'CONTAINED_ONLY (内削除)', 
        [DELETE_PHASES.CONTAINED_WITH_POUCHES]: 'CONTAINED_WITH_POUCHES (内・パ削除)',
        [DELETE_PHASES.MAIN_WITH_CONTAINED]: 'MAIN_WITH_CONTAINED (本・内削除)',
        [DELETE_PHASES.ALL_DELETE]: 'ALL_DELETE (本・内・パ削除)'
      };
    }
    
    // 削除状態の可視化
    if (!save) {
      // 可視化のみ（保存しない）
      
      // まず、すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      
      const targets = checkDeleteTargets();
      // checkDeleteTargetsと同じ方法でパウチを検出
      const pouchesInGroup = pouches.filter(p => {
        const isInGroupPouches = group.pouches && group.pouches.includes(p.id);
        // groupIdsは廃止済み
        return isInGroupPouches;
      });
      const containedGroups = getContainedGroups(group, allGroups);
      
      // 削除対象の要素にクラスを追加/削除
      switch (phase) {
        case DELETE_PHASES.MAIN_ONLY:
          // 0秒: 本削除
          const groupElement = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement) {
            groupElement.classList.add('delete-preview');
          }
          // 他の要素は通常状態に戻す
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.remove('delete-preview');
            }
          });
          pouchesInGroup.forEach(pouch => {
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.remove('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.CONTAINED_ONLY:
          // 1秒: 内削除
          // 本グループの削除プレビューを削除
          const groupElement1 = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement1) {
            groupElement1.classList.remove('delete-preview');
          }
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.CONTAINED_WITH_POUCHES:
          // 2秒: 内・パ削除
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          // パウチを削除状態に
          pouchesInGroup.forEach(pouch => {
            // 複数のセレクターを試す
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.MAIN_WITH_CONTAINED:
          // 3秒: 本・内削除（パ復活）
          const groupElement2 = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement2) {
            groupElement2.classList.add('delete-preview');
          }
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          // パウチは通常状態に戻す
          pouchesInGroup.forEach(pouch => {
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.remove('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.ALL_DELETE:
          // 4秒: 本・内・パ削除
          // 本グループを削除状態に
          const groupElement4 = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement4) {
            groupElement4.classList.add('delete-preview');
          }
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          // パウチを削除状態に
          pouchesInGroup.forEach(pouch => {
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
          break;
      }
    }
    
    if (save) {
      // 実際の削除処理を実行
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      let isCtrlOptionPressed = false;
      let isShiftPressed = false;
      let isAltControlPressed = false;
      
      switch (phase) {
        case DELETE_PHASES.MAIN_ONLY:
          // 0秒: 本削除（通常削除）
          break;
        case DELETE_PHASES.CONTAINED_ONLY:
          // 1秒: 内削除（Shift+Alt/Control+削除と同じ）
          isShiftPressed = true;
          isAltControlPressed = true; // Mac: Ctrl, Windows: Alt
          break;
        case DELETE_PHASES.CONTAINED_WITH_POUCHES:
          // 2秒: 内・パ削除（Alt/Control+削除と同じ）
          isAltControlPressed = true; // Mac: Ctrl, Windows: Alt
          break;
        case DELETE_PHASES.MAIN_WITH_CONTAINED:
          // 3秒: 本・内削除（Shift+Ctrl/Option+削除と同じ）
          isShiftPressed = true;
          isCtrlOptionPressed = true; // Mac: Option, Windows: Ctrl
          break;
        case DELETE_PHASES.ALL_DELETE:
          // 4秒: 本・内・パ削除（Ctrl/Option+削除と同じ）
          isCtrlOptionPressed = true; // Mac: Option, Windows: Ctrl
          break;
      }
      
      // 実際の削除処理実行
      
      onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
    }
  }, [group.id, onDeleteGroup]);
  
  // 削除タイマーのクリア
  const clearDeleteTimers = useCallback(() => {
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    deletePhaseTimersRef.current.forEach(timerId => {
      window.clearTimeout(timerId);
    });
    deletePhaseTimersRef.current = [];
    
    deleteStartTimeRef.current = 0;
    deleteCurrentPhaseRef.current = 0;
    deleteModifierKeysRef.current = null;
    
    // 削除プレビューのクラスをクリア
    const allElements = document.querySelectorAll('.delete-preview');
    allElements.forEach(element => {
      element.classList.remove('delete-preview');
    });
  }, []);

  // このグループに関連するGTPのIDを取得（グループ外のGTPも含む）
  const relatedGtpIds = useMemo(() => {
    return allPouches
      .filter(pouch => pouch.isGroupTotalPouch && pouch.referencedGroupId === group.id)
      .map(pouch => pouch.id);
  }, [allPouches, group.id, group._isDragging]);


  // ホバー状態のクラス名を生成
  const groupClassName = useMemo(() => {
    const classes = [];
    
    // このグループがホバーされている場合
    if (hoveredGroupId === group.id) {
      classes.push('group-hovered');
    }
    
    // ドラッグ中のクラスは手動で管理するため、ここでは追加しない
    
    // GTPホバー時のクラスはActiveSheetComponentから直接適用されるため、ここでは処理しない
    
    return classes.join(' ');
  }, [hoveredGroupId, group.id]);

  
  


  


  // ホバーイベントハンドラー（操作ガイド: 2 秒で tier 2）
  const handleMouseEnter = useCallback(() => {
    onGroupHover(group.id);
    setGroupGuideHoverTier(1);
    if (groupGuideHoverTimerRef.current !== null) {
      window.clearTimeout(groupGuideHoverTimerRef.current);
    }
    if (_isGuideEnabled) {
      groupGuideHoverTimerRef.current = window.setTimeout(() => {
        setGroupGuideHoverTier(2);
        groupGuideHoverTimerRef.current = null;
      }, GUIDE_HOVER_DELAY_MS);
    } else {
      groupGuideHoverTimerRef.current = null;
    }
  }, [onGroupHover, group.id, _isGuideEnabled]);

  const handleMouseLeave = useCallback(() => {
    if (groupGuideHoverTimerRef.current !== null) {
      window.clearTimeout(groupGuideHoverTimerRef.current);
      groupGuideHoverTimerRef.current = null;
    }
    setGroupGuideHoverTier(0);
    onGroupHover(null);
  }, [onGroupHover]);

  const clearResizeGuideTimer = useCallback(() => {
    if (resizeGuideHoverTimerRef.current !== null) {
      window.clearTimeout(resizeGuideHoverTimerRef.current);
      resizeGuideHoverTimerRef.current = null;
    }
  }, []);

  const onResizeGuideMouseEnter = useCallback(() => {
    resizeGuideHoverDepthRef.current += 1;
    if (resizeGuideHoverDepthRef.current === 1) {
      setResizeGuideHoverTier(1);
      clearResizeGuideTimer();
      if (_isGuideEnabled) {
        resizeGuideHoverTimerRef.current = window.setTimeout(() => {
          setResizeGuideHoverTier(2);
          resizeGuideHoverTimerRef.current = null;
        }, GUIDE_HOVER_DELAY_MS);
      }
    }
  }, [clearResizeGuideTimer, _isGuideEnabled]);

  const onResizeGuideMouseLeave = useCallback(() => {
    resizeGuideHoverDepthRef.current -= 1;
    if (resizeGuideHoverDepthRef.current <= 0) {
      resizeGuideHoverDepthRef.current = 0;
      clearResizeGuideTimer();
      setResizeGuideHoverTier(0);
    }
  }, [clearResizeGuideTimer]);
  
  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
      if (groupGuideHoverTimerRef.current !== null) {
        window.clearTimeout(groupGuideHoverTimerRef.current);
      }
      if (resizeGuideHoverTimerRef.current !== null) {
        window.clearTimeout(resizeGuideHoverTimerRef.current);
      }
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
      }
      // 削除ボタンのタイマーもクリア
      clearDeleteTimers();
    };
  }, [clearDeleteTimers]);
  
  // タイトルが変更された時にeditTitleを更新
  useEffect(() => {
    setEditTitle(group.title || '');
  }, [group.title]);

  useEffect(() => {
    if (!isTitleEditing) {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
      setEditGuideTier(1);
      return;
    }
    setEditGuideTier(1);
    if (editGuideTimerRef.current !== null) {
      window.clearTimeout(editGuideTimerRef.current);
      editGuideTimerRef.current = null;
    }
    if (_isGuideEnabled) {
      editGuideTimerRef.current = window.setTimeout(() => {
        setEditGuideTier(2);
        editGuideTimerRef.current = null;
      }, GUIDE_HOVER_DELAY_MS);
    }
    return () => {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
    };
  }, [isTitleEditing, _isGuideEnabled]);
  
  // パスの境界がない場合はレンダリングしない
  if (!pathBounds) return null;

  // タイトル変更時のハンドラ
  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditTitle(e.target.value);
    setIsTitleEditing(true);
  };
  
  // タイトル編集確定
  const handleTitleConfirm = () => {
    if (!isTitleEditing) return;
    
    onUpdateGroupTitle(group.id, editTitle);
    setIsTitleEditing(false);
    
    // 確定フラグを設定してタイムアウト後にリセット
    justConfirmedRef.current = true;
    
    // 前回のタイマーがあれば解除
    if (confirmTimeoutRef.current !== null) {
      window.clearTimeout(confirmTimeoutRef.current);
    }
    
    // ActiveSheetComponentの確定フラグも設定する
    onEditConfirm();
    
    // 確定フラグを一定時間後にリセット
    confirmTimeoutRef.current = window.setTimeout(() => {
      justConfirmedRef.current = false;
      confirmTimeoutRef.current = null;
    }, 300);
  };

  const handleTitleBlur = () => {
    if (titleEscapeCancelRef.current) {
      titleEscapeCancelRef.current = false;
      return;
    }
    handleTitleConfirm();
  };
  
  // キー入力処理
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // IME入力中は何もしない（確定待ち）
    if (isComposing) {
      return;
    }
    
    if (e.key === 'Enter') {
      // Shift+Enterで改行を入力
      if (e.shiftKey) {
        return; // デフォルトの改行動作を許可
      }
      e.preventDefault();
      e.stopPropagation();
      handleTitleConfirm();
      titleInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      titleEscapeCancelRef.current = true;
      setEditTitle(group.title || '');
      setIsTitleEditing(false);
      titleInputRef.current?.blur();
    }
  };


  // 書き出しボタンのクリックハンドラー
  const handleExportGroup = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    onExportGroup(group.id);
  };

  // 正負転換ボタンのクリックハンドラー
  const handleToggleGroupSign = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    onToggleGroupSign(group.id);
  };

  // カラー変更ボタンのクリックハンドラー
  const handleChangeColor = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isShiftPressed = false;
    let isCtrlOptionPressed = false;
    
    if (e.type === 'click' || e.type === 'mousedown' || e.type === 'mouseup') {
      // マウスイベントの場合
      const mouseEvent = e as React.MouseEvent;
      isShiftPressed = mouseEvent.shiftKey || false;
      isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
    } else {
      // タッチイベントの場合
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
    }
    
    
    onChangeGroupColor(group.id, isShiftPressed, isCtrlOptionPressed);
  };

  // 短押し削除: 修飾キー状態を取得（二段階タップの確定時に使用）
  const getDeleteModifiersFromEndEvent = (
    e: React.MouseEvent | React.TouchEvent,
    eventType: 'mouse' | 'touch'
  ) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    if (eventType === 'mouse') {
      const mouseEvent = e as React.MouseEvent;
      return {
        ctrlOption: isMac ? mouseEvent.altKey : mouseEvent.ctrlKey,
        shift: mouseEvent.shiftKey || false,
        altControl: isMac ? mouseEvent.ctrlKey : mouseEvent.altKey,
      };
    }
    return {
      ctrlOption: isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed,
      shift: (window as any).__shiftKeyPressed || false,
      altControl: isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed,
    };
  };

  const handleShortPressDeleteTwoStage = (
    e: React.MouseEvent | React.TouchEvent,
    eventType: 'mouse' | 'touch'
  ) => {
    const { ctrlOption, shift, altControl } = getDeleteModifiersFromEndEvent(e, eventType);
    handleConfirmingDeleteButtonRelease(deleteButtonRef.current, () => {
      onDeleteGroup(group.id, ctrlOption, shift, altControl);
    });
  };

  // 共通の削除開始処理（マウス・タッチ共通）
  const handleDeleteStart = (e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    e.preventDefault();

    // タッチのあとに送られる合成 mousedown が deleteStartTimeRef を立て直し、
    // 続く mouseup で二段階削除の確定が走るため無視する
    if (eventType === 'mouse' && Date.now() < deleteSuppressMouseAfterTouchUntilRef.current) {
      return;
    }
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isCtrlOptionPressed = false;
    let isShiftPressed = false;
    let isAltControlPressed = false;
    
    if (eventType === 'mouse') {
      // マウスイベントの場合
      const mouseEvent = e as React.MouseEvent;
      isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
      isShiftPressed = mouseEvent.shiftKey || false;
      isAltControlPressed = isMac ? mouseEvent.ctrlKey : mouseEvent.altKey;
    } else {
      // タッチイベントの場合、現在の修飾キー状態をチェック
      // タッチイベントでは修飾キーの状態が直接取得できないため、
      // グローバルな修飾キー状態を追跡する必要がある
      // 簡易実装として、キーボードイベントリスナーで状態を追跡
      isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isAltControlPressed = isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed;
    }
    
    
    // 修飾キー状態を保存
    deleteModifierKeysRef.current = {
      ctrlOption: isCtrlOptionPressed,
      shift: isShiftPressed,
      altControl: isAltControlPressed
    };
    
    deleteStartTimeRef.current = Date.now();
    
    // 修飾キーが押されている場合は長押し時のみプレビュー（短押しは二段階タップ）
    if (deleteModifierKeysRef.current) {
      const { ctrlOption, shift, altControl } = deleteModifierKeysRef.current;
      
      if (ctrlOption || shift || altControl) {
        deleteLongPressTimerRef.current = window.setTimeout(() => {
          let targetPhase = DELETE_PHASES.MAIN_ONLY;
          
          if (ctrlOption && !shift && !altControl) {
            targetPhase = DELETE_PHASES.ALL_DELETE; // Ctrl/Option: 本・内・パ削除
          } else if (shift && ctrlOption && !altControl) {
            targetPhase = DELETE_PHASES.MAIN_WITH_CONTAINED; // Shift+Ctrl/Option: 本・内削除
          } else if (altControl && !shift && !ctrlOption) {
            targetPhase = DELETE_PHASES.CONTAINED_WITH_POUCHES; // Alt/Control: 内・パ削除
          } else if (shift && altControl && !ctrlOption) {
            targetPhase = DELETE_PHASES.CONTAINED_ONLY; // Shift+Alt/Control: 内削除
          }
          
          applyDeletePhase(targetPhase, false); // false = 保存しない
          deletePhaseTimersRef.current = [];
        }, 1000);
        return;
      }
    }
    
    // 修飾キーなし: 長押し検出タイマーを開始（短押しは二段階タップのため即プレビューしない）
    deleteLongPressTimerRef.current = window.setTimeout(() => {
      // 長押し検出
      
      // すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      
      // 長押しが検出された場合の処理
      const targets = checkDeleteTargets();
      
      // 通常の段階的削除処理
      const phases: number[] = [];
      if (targets.canDeleteMain) phases.push(DELETE_PHASES.MAIN_ONLY);
      if (targets.canDeleteContained) phases.push(DELETE_PHASES.CONTAINED_ONLY);
      if (targets.canDeleteContained || targets.canDeletePouches) phases.push(DELETE_PHASES.CONTAINED_WITH_POUCHES); // 内包グループまたはパウチがある場合
      if (targets.canDeleteMain && targets.canDeleteContained) phases.push(DELETE_PHASES.MAIN_WITH_CONTAINED);
      if (targets.canDeleteMain && (targets.canDeleteContained || targets.canDeletePouches)) phases.push(DELETE_PHASES.ALL_DELETE); // 本グループと（内包グループまたはパウチ）がある場合
      
      
      // 即座に最初のフェーズを表示
      if (phases.length > 0) {
        applyDeletePhase(phases[0], false);
      }
      
      // デバッグログを削除
      
      // フェーズを順次実行（最初のフェーズは既に表示済み）
      const phaseTimers: number[] = [];
      phases.forEach((phase, index) => {
        // 最初のフェーズ（index=0）は既に表示済みなのでスキップ
        if (index === 0) return;
        
        const delay = (index - 1) * 1000; // 0秒、1秒、2秒、3秒...（最初のフェーズは既に表示済み）
        const phaseNames = {
          [DELETE_PHASES.MAIN_ONLY]: 'MAIN_ONLY (本削除)',
          [DELETE_PHASES.CONTAINED_ONLY]: 'CONTAINED_ONLY (内削除)',
          [DELETE_PHASES.CONTAINED_WITH_POUCHES]: 'CONTAINED_WITH_POUCHES (内・パ削除)',
          [DELETE_PHASES.MAIN_WITH_CONTAINED]: 'MAIN_WITH_CONTAINED (本・内削除)',
          [DELETE_PHASES.ALL_DELETE]: 'ALL_DELETE (本・内・パ削除)'
        };
        
        const timerId = window.setTimeout(() => {
          applyDeletePhase(phase, false); // false = 保存しない
        }, delay);
        
        phaseTimers.push(timerId);
      });
      
      deletePhaseTimersRef.current = phaseTimers;
    }, 1000); // 1秒で長押し検出
  };

  // 削除ボタンのマウスダウン処理
  const handleDeleteMouseDown = (e: React.MouseEvent) => {
    handleDeleteStart(e, 'mouse');
  };

  // 削除ボタンのタッチスタート処理
  const handleDeleteTouchStart = (e: React.TouchEvent) => {
    handleDeleteStart(e, 'touch');
  };
  
  // 共通の削除終了処理（マウス・タッチ共通）
  const handleDeleteEnd = (e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();

    if (eventType === 'mouse' && Date.now() < deleteSuppressMouseAfterTouchUntilRef.current) {
      if (deleteLongPressTimerRef.current !== null) {
        window.clearTimeout(deleteLongPressTimerRef.current);
        deleteLongPressTimerRef.current = null;
      }
      clearDeleteTimers();
      return;
    }
    
    
    // 長押しタイマーをクリア
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    // 長押しが検出されていた場合
    if (deleteStartTimeRef.current > 0) {
      const holdDuration = Date.now() - deleteStartTimeRef.current;
      
      if (holdDuration >= 1000) {
        // 長押しが検出されていた場合
        
        // 修飾キーが押されている場合は、修飾キー相当のフェーズで削除
        if (deleteModifierKeysRef.current) {
          const { ctrlOption, shift, altControl } = deleteModifierKeysRef.current;
          
          if (ctrlOption || shift || altControl) {
            let targetPhase = DELETE_PHASES.MAIN_ONLY;
            
            if (ctrlOption && !shift && !altControl) {
              targetPhase = DELETE_PHASES.ALL_DELETE; // Ctrl/Option: 本・内・パ削除
            } else if (shift && ctrlOption && !altControl) {
              targetPhase = DELETE_PHASES.MAIN_WITH_CONTAINED; // Shift+Ctrl/Option: 本・内削除
            } else if (altControl && !shift && !ctrlOption) {
              targetPhase = DELETE_PHASES.CONTAINED_WITH_POUCHES; // Alt/Control: 内・パ削除
            } else if (shift && altControl && !ctrlOption) {
              targetPhase = DELETE_PHASES.CONTAINED_ONLY; // Shift+Alt/Control: 内削除
            }
            
            applyDeletePhase(targetPhase, true); // true = 保存する
            return;
          }
        }
        
        // 修飾キーが押されていない場合は、長押し時間に基づいて正しいフェーズを決定
        const targets = checkDeleteTargets();
        const phases: number[] = [];
        if (targets.canDeleteMain) phases.push(DELETE_PHASES.MAIN_ONLY);
        if (targets.canDeleteContained) phases.push(DELETE_PHASES.CONTAINED_ONLY);
        if (targets.canDeleteContained || targets.canDeletePouches) phases.push(DELETE_PHASES.CONTAINED_WITH_POUCHES); // 内包グループまたはパウチがある場合
        if (targets.canDeleteMain && targets.canDeleteContained) phases.push(DELETE_PHASES.MAIN_WITH_CONTAINED);
        if (targets.canDeleteMain && (targets.canDeleteContained || targets.canDeletePouches)) phases.push(DELETE_PHASES.ALL_DELETE); // 本グループと（内包グループまたはパウチ）がある場合
        
        // 長押し時間に基づいて、実際に実行可能なフェーズを決定
        let nextPhase = phases[0]; // デフォルト（最初のフェーズ）
        if (holdDuration >= 4000 && phases.length > 4) {
          nextPhase = phases[4]; // 4秒以上
        } else if (holdDuration >= 3000 && phases.length > 3) {
          nextPhase = phases[3]; // 3秒以上
        } else if (holdDuration >= 2000 && phases.length > 2) {
          nextPhase = phases[2]; // 2秒以上
        } else if (holdDuration >= 1000 && phases.length > 1) {
          nextPhase = phases[1]; // 1秒以上
        }
        
        // まず正しいプレビューを表示
        applyDeletePhase(nextPhase, false); // false = プレビューのみ
        // その後実際の削除を実行
        applyDeletePhase(nextPhase, true); // true = 保存する
      } else {
        // 短いクリックの場合、二段階タップで削除
        handleShortPressDeleteTwoStage(e, eventType);
      }
    } else {
      // 通常のクリック処理（短いクリック）— 二段階タップで削除
      handleShortPressDeleteTwoStage(e, eventType);
    }
    
    // タイマーをクリア
    clearDeleteTimers();

    // タッチ完了後の合成マウス列を無視（mousedown が来ると ref が再設定される）
    if (eventType === 'touch') {
      deleteSuppressMouseAfterTouchUntilRef.current = Date.now() + 550;
    }
  };

  // 削除ボタンのマウスアップ処理
  const handleDeleteMouseUp = (e: React.MouseEvent) => {
    handleDeleteEnd(e, 'mouse');
  };

  // 削除ボタンのタッチエンド処理
  const handleDeleteTouchEnd = (e: React.TouchEvent) => {
    handleDeleteEnd(e, 'touch');
  };

  // 削除ボタンのマウスリーブ処理
  const handleDeleteMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (Date.now() < deleteSuppressMouseAfterTouchUntilRef.current) {
      return;
    }
    
    // 長押しタイマーをクリア
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    // マウスリーブ時は削除をキャンセル（何も起きない）
    
    // タイマーをクリア
    clearDeleteTimers();
  };

  // 削除ボタンのタッチキャンセル処理
  const handleDeleteTouchCancel = (e: React.TouchEvent) => {
    e.stopPropagation();
    deleteSuppressMouseAfterTouchUntilRef.current = Date.now() + 550;
    
    // 長押しタイマーをクリア
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    // タッチキャンセル時は削除をキャンセル（何も起きない）
    
    // タイマーをクリア
    clearDeleteTimers();
  };
  
  // 削除ボタンのクリックハンドラー（従来の処理）
  const handleDeleteGroup = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    // getCtrlOptionModifierKey: Mac: Option, Windows: Ctrl
    const isCtrlOptionPressed = isMac ? (e as React.MouseEvent).altKey : (e as React.MouseEvent).ctrlKey;
    const isShiftPressed = (e as React.MouseEvent).shiftKey || false;
    // getAltControlModifierKey: Mac: Ctrl, Windows: Alt
    const isAltControlPressed = isMac ? (e as React.MouseEvent).ctrlKey : (e as React.MouseEvent).altKey;
    
    onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
  };

  // サブトータルクリックハンドラー（グループトータルパウチ作成）
  const handleSubtotalClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    
    // クリック位置を取得
    let clientX: number, clientY: number;
    if ('touches' in e) {
      // タッチイベントの場合
      if (e.touches.length > 0) {
        // onTouchStartの場合
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if ('changedTouches' in e && e.changedTouches.length > 0) {
        // onTouchEndの場合
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        return; // タッチ位置が取得できない場合は処理を中断
      }
    } else {
      // マウスイベントの場合
      const mouseEvent = e as React.MouseEvent;
      clientX = mouseEvent.clientX;
      clientY = mouseEvent.clientY;
    }
    
    onCreateGroupTotalPouch(group.id, clientX, clientY);
  };
  
  return (
    <div 
      className={`group-wrapper ${group._isCloning ? 'cloning' : ''} ${(!group.pouches || group.pouches.length === 0) ? 'no-pouches' : ''} ${isTableSelected ? 'table-selected' : ''} ${group.color === 'mute' ? 'mute-group' : ''} ${group._isInMuteGroup ? 'in-mute-group' : ''} ${group._isAdding ? 'group-adding' : ''} ${group._isDeleting ? 'group-deleting' : ''} ${group._isDragging ? 'dragging-flag' : ''} ${groupClassName}`}
      data-group-id={group.id}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        '--group-color': group.color === 'mute' ? '#666666' : (GROUP_COLORS.find(color => color.id === group.color)?.value || GROUP_COLORS[0].value)
      } as React.CSSProperties}
    >
      {/* SVG要素 */}
      <svg
        className="group-svg"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible'
        }}
      >
        <path
          d={group.path}
          className="group-path"
          data-group-id={group.id}
        />
      </svg>
      
      {/* パスと同じ位置・サイズのコンテナ */}
      <div 
        className={`group-container ${isTotalGroup ? 'total-group' : ''}`}
        data-deleting={group._isDeleting ? 'true' : 'false'}
        {...(groupContainerTooltipHtml
          ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupContainerTooltipHtml }
          : {})}
        style={{
          position: 'absolute',
          left: pathBounds.left,
          top: pathBounds.top,
          width: pathBounds.right - pathBounds.left,
          height: pathBounds.bottom - pathBounds.top,
          pointerEvents: 'all'
        }}
        onMouseDown={(e) => {
          onMouseDown(e, group.id, 'group');
        }}
        onTouchStart={(e) => {
          onTouchStart(e, group.id, 'group');
        }}
        onMouseUp={(e) => onMouseUp(e as unknown as MouseEvent)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* グループタイトル入力欄 - 左上 */}
        <div className="group-title-container">
          <textarea
            ref={titleInputRef}
            className={`group-title-input${editTitle.length >= 30 ? ' long-title' : ''}`}
            {...(isTitleEditing
              ? groupEditModeTooltipHtml
                ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupEditModeTooltipHtml }
                : {}
              : groupTitleTooltipHtml
                ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupTitleTooltipHtml }
                : {})}
            placeholder={(() => {
              if (isTotalGroup && sheetTitle) {
                // TOTAL表示の条件を満たす場合は、シート名をプレースホルダーとして使用
                return sheetTitle;
              }
              return group.placeholderText || t.table.groupN(getGroupSerialNumber(group.id, allGroups));
            })()}
            value={editTitle}
            onChange={handleTitleChange}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onKeyDown={handleKeyDown}
            onBlur={handleTitleBlur}
            rows={1}
            onMouseDown={(e) => {
              wasTitleFocusedOnMouseDown.current = document.activeElement === e.currentTarget;
              if (wasTitleFocusedOnMouseDown.current) {
                e.stopPropagation();
              }
            }}
            onMouseMove={(e) => {
              if (wasTitleFocusedOnMouseDown.current) {
                e.stopPropagation();
              }
            }}
            onMouseUp={(e) => {
              if (wasTitleFocusedOnMouseDown.current) {
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!group._isDragging) {
                const target = e.currentTarget;
                target.focus();
                target.select();
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              const wasFocused = document.activeElement === target;
              if (!wasFocused) {
                requestAnimationFrame(() => {
                  target.focus();
                  target.select();
                });
              }
            }}
          />
        </div>
        
        {/* グループ操作ボタン - 右上に縦に並べる */}
        <div className="group-buttons">
          {/* 書き出しボタン - 1番目（一番上） */}
          <button 
            className="group-export-button"
            data-tooltip-id="tt" data-tooltip-html={groupExportTooltipHtml}
            onClick={handleExportGroup}
            onTouchEnd={(e) => {
              const isDragging = onCheckGroupDragging ? onCheckGroupDragging(group.id) : group._isDragging;
              if (!isDragging) {
                handleExportGroup(e);
              }
            }}
            disabled={!group.pouches || group.pouches.length === 0}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 6l-4-4-4 4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 2v13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 正負転換ボタン - 2番目 */}
          <button 
            className="group-toggle-sign-button"
            data-tooltip-id="tt" data-tooltip-html={groupToggleSignTooltipHtml}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.type === 'click' && e.clientX === 0 && e.clientY === 0) {
                // タッチイベントから発火された人工的なクリックは無視
                return;
              }
              handleToggleGroupSign(e);
            }}
            onTouchEnd={(e) => {
              const isDragging = onCheckGroupDragging ? onCheckGroupDragging(group.id) : group._isDragging;
              if (!isDragging) {
                e.preventDefault();
                e.stopPropagation();
                handleToggleGroupSign(e);
              }
            }}
            disabled={!group.pouches || group.pouches.length === 0}
          >
            <svg 
              viewBox="0 0 150 150" 
              fill="var(--icon-color)"
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M77.955,109.975c-4.037,0-7.309,3.271-7.309,7.308c0,4.037,3.272,7.31,7.309,7.31h56.029c4.037,0,7.309-3.271,7.309-7.31
                c0-4.036-3.271-7.308-7.309-7.308H77.955z"/>
              <path d="M0,44.2c0,4.037,3.272,7.308,7.309,7.308h19.489v19.49c0,4.036,3.271,7.309,7.308,7.309c4.037,0,7.309-3.271,7.309-7.309
                v-19.49H63.34c4.037,0,7.308-3.271,7.308-7.308c0-4.037-3.271-7.309-7.308-7.309H41.414V12.53c0-4.037-3.272-7.308-7.309-7.308
                c-4.037,0-7.308,3.271-7.308,7.308v24.361H7.308C3.272,36.892,0,40.164,0,44.2z"/>
              <path d="M131.428,17.261c-2.854-2.855-7.481-2.855-10.336,0L14.763,123.594c-2.853,2.854-2.853,7.481,0,10.334
                c1.425,1.429,3.298,2.143,5.167,2.143c1.868,0,3.742-0.714,5.169-2.143L131.428,27.599
                C134.283,24.746,134.283,20.114,131.428,17.261z"/>
            </svg>
          </button>

          {/* カラー変更ボタン - 3番目 */}
          <button 
            className="group-color-button"
            data-tooltip-id="tt" data-tooltip-html={groupColorButtonTooltipHtml}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleColorStart(e, 'mouse');
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleColorEnd(e, 'mouse');
            }}
            onMouseLeave={handleColorCancel}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleColorStart(e, 'touch');
            }}
            onTouchEnd={(e) => {  
              e.preventDefault();
              e.stopPropagation();
              handleColorEnd(e, 'touch');
            }}
            onTouchCancel={handleColorCancel}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.type === 'click' && e.clientX === 0 && e.clientY === 0) {
                // タッチイベントから発火された人工的なクリックは無視
                return;
              }
            }}
          >
            <svg 
              viewBox="0 0 1024 1024" 
              version="1.1" 
              xmlns="http://www.w3.org/2000/svg"
              fill="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M833.706667 216.746667A452.693333 452.693333 0 0 0 508.16 85.333333a426.666667 426.666667 0 0 0-2.133333 853.333334 110.08 110.08 0 0 0 107.946666-80.64 107.52 107.52 0 0 0-24.32-97.28 21.333333 21.333333 0 0 1 15.786667-35.413334h70.4A262.4 262.4 0 0 0 938.666667 483.413333a361.813333 361.813333 0 0 0-104.96-266.666666z m-541.866667 412.16a64 64 0 1 1 17.066667-88.746667 63.573333 63.573333 0 0 1-17.066667 88.746667zM354.133333 394.666667a64 64 0 1 1-23.466666-85.333334 64 64 0 0 1 23.466666 85.333334zM469.333333 298.666667a64 64 0 1 1 64-64A64 64 0 0 1 469.333333 298.666667z m245.333334 34.133333a64 64 0 1 1 23.466666-85.333333 64 64 0 0 1-23.466666 85.333333z" />
            </svg>
          </button>

          {/* マージボタン - 4番目 */}
          <button 
            className={`group-merge-button ${isMergeDisabled ? 'disabled' : ''}`}
            data-tooltip-id="tt" data-tooltip-html={groupMergeTooltipHtml}
            onMouseDown={(e) => handleMergeStart(e, 'mouse')}
            onMouseUp={(e) => handleMergeEnd(e, 'mouse')}
            onMouseLeave={handleMergeCancel}
            onTouchStart={(e) => handleMergeStart(e, 'touch')}
            onTouchEnd={(e) => handleMergeEnd(e, 'touch')}
            onTouchCancel={() => {
              mergeSuppressMouseAfterTouchUntilRef.current = Date.now() + 550;
              handleMergeCancel();
            }}
            onContextMenu={(e) => {
              e.preventDefault(); // 右クリックメニューを無効化
            }}
            disabled={isMergeDisabled}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M21 12L15 12L10 12C10 12 6 12 3 7M10 12C10 12 6 12 3 17" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 8L21 12L16 16" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 削除ボタン - 5番目（一番下） */}
          <button 
            ref={deleteButtonRef}
            className="group-delete-button"
            data-tooltip-id="tt" data-tooltip-html={groupDeleteButtonTooltipHtml}
            onMouseDown={handleDeleteMouseDown}
            onMouseUp={handleDeleteMouseUp}
            onMouseLeave={handleDeleteMouseLeave}
            onTouchStart={handleDeleteTouchStart}
            onTouchEnd={handleDeleteTouchEnd}
            onTouchCancel={handleDeleteTouchCancel}
            onContextMenu={(e) => {
              e.preventDefault(); // 右クリックメニューを無効化
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>

        {/* Subtotal */}
        <div 
          className="group-subtotal clickable-subtotal"
          onMouseUp={(e) => {
            // ドラッグ中でない場合のみGTP作成
              e.stopPropagation(); // 親のhandleMouseUpが実行されないようにする
              e.preventDefault(); // デフォルトの動作を防ぐ
              // デバッグ情報を追加
              onResetInteraction(); // ドラッグ状態をリセット
              // 新規パウチ作成と同じロジックを使用
              onCreateGroupTotalPouch(group.id, e.clientX, e.clientY);
          }}
            onTouchEnd={(e) => {
                const isDragging = onCheckGroupDragging ? onCheckGroupDragging(group.id) : group._isDragging;
                if (!isDragging) {
                  const touch = e.changedTouches[0];
                  if (touch) {
                      onCreateGroupTotalPouch(group.id, touch.clientX, touch.clientY);
                  }
                } else {
                }
            }}
          data-tooltip-id="tt" data-tooltip-html={groupSubtotalCompositeTooltipHtml}
        >
          {isTotalGroup ? t.group.total : t.group.subtotal}{' '}
            {(() => (
              <LayerAwareCounter
                sheet={currentSheet}
                value={Math.abs(groupSubtotalContribution)}
                isNegative={groupSubtotalContribution < 0}
                animateFromZero={isInitialLoading || isSheetSwitching || group._previousSubtotal === 0}
                groupId={group.id}
                duration={300}
                suppressTooltip
                showTimeConversion={false}
                displayAsTime={showTimeConversion}
                timeDisplayTag="strong"
              />
            ))()}
        </div>
        
        {/* リサイズハンドル - 右下（タッチ・非タッチ共通） */}
        <div
          className="resize-handle resize-handle-br"
          data-group-id={group.id}
          data-resize-corner="br"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation(); // 親要素へのイベント伝播を防止
            onMouseDown(e, group.id, 'resize-handle');
          }}
          onTouchStart={(e) => {
            e.stopPropagation(); // 親要素へのイベント伝播を防止
            onTouchStart(e, group.id, 'resize-handle');
          }}
        />
        {/* リサイズハンドル - 左下（非タッチデバイスのみ） */}
        <div
          className="resize-handle resize-handle-bl"
          data-group-id={group.id}
          data-resize-corner="bl"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation();
            onMouseDown(e, group.id, 'resize-handle');
          }}
        />
        {/* リサイズハンドル - 右上（非タッチデバイスのみ） */}
        <div
          className="resize-handle resize-handle-tr"
          data-group-id={group.id}
          data-resize-corner="tr"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation();
            onMouseDown(e, group.id, 'resize-handle');
          }}
        />
        {/* リサイズハンドル - 左上（非タッチデバイスのみ） */}
        <div
          className="resize-handle resize-handle-tl"
          data-group-id={group.id}
          data-resize-corner="tl"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation();
            onMouseDown(e, group.id, 'resize-handle');
          }}
        />
      </div>
    </div>
  );
};

// 描画中のグループを表示するコンポーネント
export const PendingGroupComponent: React.FC<PendingGroupProps> = ({
  pendingPath,
  lastGroupColor,
  isTooSmall = false
}) => {
  if (!pendingPath) return null;
    
  return (
    <div
      className={`group-wrapper pending-group-wrapper ${isTooSmall ? 'too-small' : ''}`}
      style={{
        '--group-color': lastGroupColor === 'mute' ? '#666666' : (GROUP_COLORS.find(color => color.id === lastGroupColor)?.value || GROUP_COLORS[0].value)
      } as React.CSSProperties}
    >
      <svg
        className="group-svg"
      >
        <path
          d={pendingPath}
          className={`group-path ${isTooSmall ? 'to-small' : ''}`}
        />
      </svg>
    </div>
  );
};

/** このグループの表示に効くパウチ ID（所属・自グループ参照 GTP・参照先グループのメンバー） */
function collectRelevantPouchIds(
  group: Group,
  allPouches: Pouch[],
  allGroups: Group[]
): Set<string> {
  const ids = new Set<string>();
  for (const id of group.pouches ?? []) {
    ids.add(id);
  }
  for (const p of allPouches) {
    if (p.isGroupTotalPouch && p.referencedGroupId === group.id) {
      ids.add(p.id);
    }
  }
  for (const id of group.pouches ?? []) {
    const p = allPouches.find(x => x.id === id);
    if (p?.isGroupTotalPouch && p.referencedGroupId) {
      const ref = allGroups.find(g => g.id === p.referencedGroupId);
      for (const mid of ref?.pouches ?? []) {
        ids.add(mid);
      }
    }
  }
  return ids;
}

function layerValuesEqual(
  a?: Pouch['layerValues'],
  b?: Pouch['layerValues']
): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(k => {
    const va = a[k];
    const vb = b[k];
    return (
      va?.value === vb?.value &&
      va?.lastExpression === vb?.lastExpression &&
      va?.valueWhenLastExpression === vb?.valueWhenLastExpression
    );
  });
}

function pouchFieldsEqual(a: Pouch | undefined, b: Pouch | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.id !== b.id) return false;
  return (
    a.value === b.value &&
    a.multiplier === b.multiplier &&
    a.isNegative === b.isNegative &&
    a.isGroupTotalPouch === b.isGroupTotalPouch &&
    a.referencedGroupId === b.referencedGroupId &&
    a.oppositeToTotal === b.oppositeToTotal &&
    a.isTime === b.isTime &&
    a._isDeleting === b._isDeleting &&
    a._isGtpRenderPreview === b._isGtpRenderPreview &&
    layerValuesEqual(a.layerValues, b.layerValues)
  );
}

function relevantPouchesEqual(
  group: Group,
  prevPouches: Pouch[],
  nextPouches: Pouch[],
  prevAllGroups: Group[],
  nextAllGroups: Group[]
): boolean {
  const prevIds = collectRelevantPouchIds(group, prevPouches, prevAllGroups);
  const nextIds = collectRelevantPouchIds(group, nextPouches, nextAllGroups);
  if (prevIds.size !== nextIds.size) return false;
  let equal = true;
  Array.from(prevIds).forEach(id => {
    if (!equal) return;
    if (!nextIds.has(id)) {
      equal = false;
      return;
    }
    const prev = prevPouches.find(p => p.id === id);
    const next = nextPouches.find(p => p.id === id);
    if (!pouchFieldsEqual(prev, next)) equal = false;
  });
  return equal;
}

function groupSnapshotEqual(a: Group | undefined, b: Group | undefined): boolean {
  if (!a || !b) return a === b;
  return (
    a.id === b.id &&
    a.color === b.color &&
    a.pouches?.length === b.pouches?.length &&
    (a.pouches?.every((id, i) => id === b.pouches?.[i]) ?? true)
  );
}

/** ミュート・GTP 参照先など、このグループの小計に効くグループだけ比較 */
function relevantAllGroupsEqual(
  group: Group,
  relevantPouchIds: Set<string>,
  prevPouches: Pouch[],
  nextPouches: Pouch[],
  prevAll: Group[] | undefined,
  nextAll: Group[] | undefined
): boolean {
  const prev = prevAll ?? [];
  const next = nextAll ?? [];
  const ids = new Set<string>([group.id]);
  [...prev, ...next].forEach(g => {
    if (g.color === 'mute' && g.pouches?.some(id => relevantPouchIds.has(id))) {
      ids.add(g.id);
    }
  });
  Array.from(relevantPouchIds).forEach(id => {
    const p = nextPouches.find(x => x.id === id) ?? prevPouches.find(x => x.id === id);
    if (p?.isGroupTotalPouch && p.referencedGroupId) {
      ids.add(p.referencedGroupId);
    }
  });
  let equal = true;
  Array.from(ids).forEach(gid => {
    if (!equal) return;
    if (!groupSnapshotEqual(prev.find(g => g.id === gid), next.find(g => g.id === gid))) {
      equal = false;
    }
  });
  return equal;
}

function gtpHoverRelevant(
  groupId: string,
  prevHover: string | null | undefined,
  nextHover: string | null | undefined
): boolean {
  if (prevHover === nextHover) return true;
  return prevHover !== groupId && nextHover !== groupId;
}

function sheetPropsEqual(prev?: Sheet, next?: Sheet): boolean {
  if (prev === next) return true;
  if (!prev || !next) return false;
  return (
    prev.activeLayerId === next.activeLayerId &&
    prev.layers === next.layers
  );
}

function displayTotalsEqual(a: GroupDisplayTotals, b: GroupDisplayTotals): boolean {
  return (
    a.contribution === b.contribution &&
    a.plainVisible === b.plainVisible &&
    a.valueTooltipHtml === b.valueTooltipHtml &&
    a.isTotalGroup === b.isTotalGroup &&
    a.showTimeConversion === b.showTimeConversion
  );
}

// React.memo: 無関係なパウチの値変更では再レンダーしない
export default React.memo(GroupComponent, (prevProps, nextProps) => {
  const group = nextProps.group;

  const groupPropsEqual =
    prevProps.group.id === group.id &&
    prevProps.group.path === group.path &&
    prevProps.group.color === group.color &&
    prevProps.group.originalColor === group.originalColor &&
    prevProps.group.title === group.title &&
    prevProps.group.pouches?.length === group.pouches?.length &&
    prevProps.group.pouches?.every((id, index) => id === group.pouches?.[index]) &&
    prevProps.group._isDeleting === group._isDeleting &&
    (prevProps.group as Group)._isDragging === (group as Group)._isDragging &&
    (prevProps.group as Group)._isCloning === (group as Group)._isCloning &&
    prevProps.group._isInMuteGroup === group._isInMuteGroup &&
    prevProps.group._previousSubtotal === group._previousSubtotal;

  const prevAll = prevProps.allGroups ?? [];
  const nextAll = nextProps.allGroups ?? [];
  const relevantIds = collectRelevantPouchIds(group, nextProps.pouches, nextAll);

  const pouchesEqual = relevantPouchesEqual(
    group,
    prevProps.pouches,
    nextProps.pouches,
    prevAll,
    nextAll
  );

  const allGroupsEqual = relevantAllGroupsEqual(
    group,
    relevantIds,
    prevProps.pouches,
    nextProps.pouches,
    prevProps.allGroups,
    nextProps.allGroups
  );

  return (
    groupPropsEqual &&
    displayTotalsEqual(prevProps.displayTotals, nextProps.displayTotals) &&
    pouchesEqual &&
    allGroupsEqual &&
    prevProps.lastGroupColor === nextProps.lastGroupColor &&
    prevProps.isInitialLoading === nextProps.isInitialLoading &&
    prevProps.isSheetSwitching === nextProps.isSheetSwitching &&
    prevProps.hoveredGroupId === nextProps.hoveredGroupId &&
    gtpHoverRelevant(group.id, prevProps.gtpHoveredGroupId, nextProps.gtpHoveredGroupId) &&
    prevProps.isTableSelected === nextProps.isTableSelected &&
    prevProps.isTouchDevice === nextProps.isTouchDevice &&
    prevProps.sheetTitle === nextProps.sheetTitle &&
    sheetPropsEqual(prevProps.currentSheet, nextProps.currentSheet)
  );
}); 